import { useState } from 'react';
import type { CodFieldValue, StorefrontUpsell } from '@xshop/shared';
import { trackEvent } from '../tracking/tracker';
import type { CheckoutAnswers } from './OnePageCheckout';

// The shopper's answers and the order bump, for the one COD form: the page's shared ones when its own elements collect
// them (the address block, the variant picker, the bump on the page), else this form's own. Field keys come from the
// merchant, so every map is prototype-less: `constructor` must read as absent too.

/** What the values hook needs: the page's answers if any, the bump on offer, and what a tick reports to the pixels. */
type ValuesInput = { answers: CheckoutAnswers | null; initialAcceptedOfferId?: string | null; offer: StorefrontUpsell | null; productId: string; currency: string };

// Starts an empty prototype-less map.
const emptyMap = <T,>(): Record<string, T> => Object.create(null) as Record<string, T>;

// Holds the answers, the refusals under them, and the bump's tick, with the two ways the shopper changes them.
export function useCheckoutValues({ answers, initialAcceptedOfferId, offer, productId, currency }: ValuesInput) {
  const [ownValues, setOwnValues] = useState<Record<string, CodFieldValue>>(emptyMap);
  const [errors, setErrors] = useState<Record<string, string>>(emptyMap);
  // The bump is remembered by offer id, so a different offer arriving never inherits an earlier tick.
  const [ownAcceptedId, setOwnAcceptedId] = useState<string | null>(initialAcceptedOfferId ?? null);
  const values = answers ? answers.values : ownValues;
  const acceptedOfferId = answers ? answers.acceptedOfferId : ownAcceptedId;
  const acceptedOffer = offer && offer.id === acceptedOfferId ? offer : null;

  // Writing into a field clears the message that field was carrying.
  const change = (key: string, value: CodFieldValue) => {
    if (answers) answers.setValue(key, value);
    else setOwnValues((current) => Object.assign(emptyMap<CodFieldValue>(), current, { [key]: value }));
    setErrors((current) => (current[key] ? Object.assign(emptyMap<string>(), current, { [key]: '' }) : current));
  };

  // Ticking the bump adds the server's own unit price and nothing else; unticking removes exactly that.
  const toggleBump = (accepted: boolean) => {
    if (!offer) return;
    if (answers) answers.acceptOffer(accepted ? offer.id : null);
    else setOwnAcceptedId(accepted ? offer.id : null);
    if (accepted) trackEvent('add_to_cart', { productId, value: offer.unitPrice, currency, numItems: 1 });
  };

  return { values, errors, setErrors, acceptedOffer, change, toggleBump };
}
